import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

/**
 * The cursor-following tooltip — the app's one tooltip rule.
 *
 * A tooltip anchored to its element makes the reader look away from what they
 * are pointing at: on a donut slice or a 6px workload dot the detail can land
 * half a card away from the cursor. This keeps the detail *at the mouse*,
 * because that is where the eye already is.
 *
 * Opt in by spreading `cursorTip(...)` onto any element — a div, a table row,
 * an SVG `<circle>`. One fixed element serves the whole app (mounted once in
 * AppLayout), so a hundred tipped elements cost a hundred data attributes
 * rather than a hundred React subtrees.
 *
 * Three things the implementation is careful about:
 *
 * 1. **No lag.** The position is written straight to `style.transform` inside
 *    the mousemove handler; React state only changes when the *host* under the
 *    cursor changes (eight times across a chart, not eight hundred).
 * 2. **Edge flipping needs a measured width**, and the width is only known
 *    after the new text has rendered — so the placement is re-run in a layout
 *    effect once the content lands, from the last known cursor position.
 * 3. **It can never trap the cursor** it was summoned by: `pointer-events:
 *    none` throughout, which also keeps `closest()` reading the element under
 *    the tip rather than the tip itself.
 */

const TIP_ATTR = "data-cursor-tip";
const LEAD_ATTR = "data-cursor-tip-lead";
const DELAY_ATTR = "data-cursor-tip-delay";
const HIDE_EVENT = "cursor-tip:hide";

/** Gap between the cursor and the tip's near corner. */
const PAD = 12;
/** Closest the tip may come to a viewport edge before it flips. */
const EDGE = 8;

interface CursorTipAttrs {
  "data-cursor-tip": string;
  "data-cursor-tip-lead"?: string;
  "data-cursor-tip-delay"?: string;
}

/**
 * Props that make an element a cursor-tip host. Spread the result:
 *
 * ```tsx
 * <div {...cursorTip("across 3 platforms", "58 active")} />
 * ```
 *
 * `lead` is the emphasised opening of the sentence (the mockups' `<b>`), kept
 * as a separate value rather than markup so a tip can carry a person's name
 * without any HTML ever being interpreted.
 *
 * `delayMs` holds the tip back until the cursor has settled. A tip that adds
 * detail to something already legible (a chart slice, a count) should appear
 * at once — that is what makes it feel attached to the cursor. A tip that
 * merely restores text the layout truncated must NOT: dragging the pointer
 * across a table of them would fire one per row, which reads as flicker rather
 * than as help.
 */
export const cursorTip = (text: string, lead?: string, delayMs?: number): CursorTipAttrs => ({
  [TIP_ATTR]: text,
  [LEAD_ATTR]: lead,
  [DELAY_ATTR]: delayMs ? String(delayMs) : undefined,
});

/**
 * Take the tip down now, without waiting for the cursor to move off its host.
 *
 * The layer is driven entirely by `mousemove`, which is right for hovering and
 * wrong for a trigger that opens something under the pointer: a picker opened
 * by click leaves the cursor exactly where it was, so nothing fires and the
 * tip stays parked on top of the menu it just summoned. Call this from the
 * `onOpenChange` of anything that opens over its own trigger.
 */
export const hideCursorTip = () => {
  if (typeof document !== "undefined") document.dispatchEvent(new CustomEvent(HIDE_EVENT));
};

interface Tip {
  text: string;
  lead?: string;
  delay: number;
}

const sameTip = (a: Tip | null, b: Tip | null) => a?.text === b?.text && a?.lead === b?.lead;

/**
 * Above-right of the cursor by default; flips left, then below, rather than
 * let the tip slide off the viewport. Writes `transform` directly — this runs
 * on every mousemove, and a re-render per pixel is exactly the lag the tip
 * exists to avoid.
 */
const place = (el: HTMLElement, x: number, y: number) => {
  const { offsetWidth: w, offsetHeight: h } = el;
  let left = x + PAD;
  let top = y - h - PAD;
  if (left + w > window.innerWidth - EDGE) left = x - w - PAD;
  if (left < EDGE) left = EDGE;
  if (top < EDGE) top = y + PAD * 1.5;
  el.style.transform = `translate3d(${Math.round(left)}px, ${Math.round(top)}px, 0)`;
};

const readTip = (target: EventTarget | null): Tip | null => {
  if (!(target instanceof Element)) return null;
  const host = target.closest(`[${TIP_ATTR}]`);
  const text = host?.getAttribute(TIP_ATTR);
  if (!host || !text) return null;
  return {
    text,
    lead: host.getAttribute(LEAD_ATTR) ?? undefined,
    delay: Number(host.getAttribute(DELAY_ATTR)) || 0,
  };
};

interface CursorPanelProps {
  /** Whether the panel is showing. The host owns this — usually a hover flag. */
  open: boolean;
  /**
   * Where the cursor was when it opened, from the `mouseenter` event. Without
   * it the panel has no position until the first `mousemove`, which is a frame
   * of it parked in the top-left corner.
   */
  at: { x: number; y: number } | null;
  /** Called when a scroll or a resize has moved the host out from under it. */
  onDismiss?: () => void;
  className?: string;
  children: ReactNode;
}

/**
 * The cursor-following tip's RICH sibling: same placement, same mouse anchor,
 * arbitrary content.
 *
 * `cursorTip()` carries a string and a lead, deliberately — a data attribute
 * cannot hold anything else, and that is what guarantees no value from the
 * database is ever interpreted as markup. Some cells need more than a
 * sentence: the backlog's Assigned stack answers "who owns this, who is
 * helping, and who put it on them", which is a small panel of faces and names,
 * not a comma-separated list.
 *
 * So this takes REACT CHILDREN — structured, escaped by React like any other
 * subtree, never an HTML string — and reuses `place()` above so it flips at
 * the viewport edges exactly as the text tip does. Mounted only while open
 * (one portal per showing panel, not one per tipped row), and positioned in a
 * layout effect so the first paint is already in the right place.
 */
export const CursorPanel = ({ open, at, onDismiss, className, children }: CursorPanelProps) => {
  const node = useRef<HTMLDivElement>(null);
  const pos = useRef(at ?? { x: 0, y: 0 });
  if (at) pos.current = at;

  useEffect(() => {
    if (!open) return;
    const onMove = (event: MouseEvent) => {
      pos.current = { x: event.clientX, y: event.clientY };
      if (node.current) place(node.current, event.clientX, event.clientY);
    };
    const dismiss = () => onDismiss?.();
    document.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      document.removeEventListener("mousemove", onMove);
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [open, onDismiss]);

  // Before the first paint, and again whenever the content resizes: the edge
  // flip needs the width of what is actually showing.
  useLayoutEffect(() => {
    if (open && node.current) place(node.current, pos.current.x, pos.current.y);
  }, [open, children]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      role="tooltip"
      aria-hidden
      ref={node}
      className={cn(
        "pointer-events-none fixed left-0 top-0 z-[60] max-w-[352px] rounded-md border border-border bg-popover px-3 py-2.5 text-[12.7px] leading-snug text-muted-foreground shadow-lg",
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  );
};

/**
 * The single floating element. Mount once, near the root; every
 * `cursorTip()`-tagged element in the app feeds it.
 */
const CursorTipLayer = () => {
  const node = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<Tip | null>(null);
  /** Mirrors `tip` for the handler, which must not re-subscribe per render. */
  const shown = useRef<Tip | null>(null);
  /** The tip whose delay is currently counting down, and its timer. */
  const pending = useRef<{ tip: Tip | null; timer: number | undefined }>({ tip: null, timer: undefined });
  const at = useRef({ x: 0, y: 0 });

  useEffect(() => {
    // A delayed tip's countdown. Cleared whenever the cursor reaches a
    // different host — or nothing — so crossing a table of delayed tips shows
    // none of them until the pointer actually settles on one.
    const cancelPending = () => {
      if (pending.current.timer !== undefined) window.clearTimeout(pending.current.timer);
      pending.current = { tip: null, timer: undefined };
    };

    const hide = () => {
      cancelPending();
      if (!shown.current) return;
      shown.current = null;
      setTip(null);
    };

    const show = (next: Tip) => {
      shown.current = next;
      setTip(next);
    };

    const onMove = (event: MouseEvent) => {
      const next = readTip(event.target);
      if (!next) {
        hide();
        return;
      }
      at.current = { x: event.clientX, y: event.clientY };
      if (node.current) place(node.current, event.clientX, event.clientY);

      if (sameTip(shown.current, next)) return;
      // Already counting down for this same host: let it finish rather than
      // restarting on every pixel of movement inside it.
      if (sameTip(pending.current.tip, next)) return;

      cancelPending();
      if (next.delay > 0) {
        pending.current = {
          tip: next,
          timer: window.setTimeout(() => {
            pending.current = { tip: null, timer: undefined };
            show(next);
          }, next.delay),
        };
        // Anything already showing belongs to the host the cursor has left.
        if (shown.current) {
          shown.current = null;
          setTip(null);
        }
        return;
      }
      show(next);
    };

    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseleave", hide);
    document.addEventListener(HIDE_EVENT, hide);
    // A scroll or a resize moves the host out from under a tip that is still
    // parked at the last mouse position, which reads as a stuck label.
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      cancelPending();
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", hide);
      document.removeEventListener(HIDE_EVENT, hide);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, []);

  // The flip above needs the width of the text that is showing *now*, so the
  // placement is re-run once the new content has been laid out.
  useLayoutEffect(() => {
    if (node.current && tip) place(node.current, at.current.x, at.current.y);
  }, [tip]);

  return (
    <div
      ref={node}
      role="tooltip"
      aria-hidden
      className={cn(
        // Wraps rather than running on one line: the tip now also carries full
        // ticket titles that the table truncated, and `whitespace-nowrap`
        // against a max-width does not clip a long one — it spills it straight
        // out through the border.
        // A step up in size (~10% on the type and the padding, and the same on
        // the wrap width so a long ticket title still breaks at the same
        // number of words). The tip carries detail the layout could not fit —
        // truncated titles, assignee lists — so it is read, not glanced at,
        // and 11.5px was small enough to be work.
        "pointer-events-none fixed left-0 top-0 z-[60] max-w-[352px] break-words rounded-md border border-border bg-popover px-[11px] py-[7px] text-[12.7px] font-semibold leading-snug text-muted-foreground shadow-lg transition-opacity duration-[130ms] motion-reduce:transition-none",
        tip ? "opacity-100" : "opacity-0",
      )}
    >
      {tip?.lead && <b className="text-foreground">{tip.lead}</b>}
      {tip?.lead && tip.text ? " " : null}
      {tip?.text}
    </div>
  );
};

export default CursorTipLayer;
